import React, { useState } from 'react';
import { Lock, Loader2, KeyRound } from 'lucide-react';

const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Login failed');
      } else {
        localStorage.setItem('bmp_admin_token', data.token);
        localStorage.setItem('bmp_admin_expires', data.expires_at);
        onSuccess(data.token);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-500/20 to-teal-500/20 border border-blue-400/40 mb-5">
            <KeyRound className="w-6 h-6 text-blue-200" />
          </div>
          <h1 className="text-3xl font-extralight text-white tracking-wide mb-2">Field Keeper</h1>
          <p className="text-slate-400 font-light text-sm">Private dashboard</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-2xl"
          data-testid="admin-login-form"
        >
          <label htmlFor="admin-password" className="block text-xs uppercase tracking-widest text-slate-500 mb-3 font-light">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              id="admin-password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              disabled={loading}
              data-testid="admin-password-input"
              className="w-full pl-11 pr-4 py-3.5 bg-slate-800/60 border border-slate-600/60 focus:border-blue-400/70 rounded-full text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300 disabled:opacity-60"
              placeholder="Enter password"
            />
          </div>

          {error && (
            <p className="mt-4 text-sm text-rose-300 text-center font-light" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-400 hover:to-teal-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-full text-white font-light tracking-wide transition-all duration-500 shadow-lg shadow-blue-500/20"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Verifying&hellip;</span>
              </>
            ) : (
              <span>Enter</span>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-600 font-light">
          Unauthorized access is logged.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
